import { useBehavior, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, TWAction, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react';
import Dash from '../../../common/Dash';
import { TCardBalanceData, TCardBalanceDataMessages } from '../../../types/data';
import { ECardBalanceStatus } from '../../../types/enums';
import { getInlineStyles } from '../../../utils/styles';
import { TextCommon } from '../../UI/Text/TextCommon';
import { CardBottomButtons } from './CardBottomButtons';
import { PaysendCard } from './PaysendCard';
import styles from './paysendCard.module.css';
import { useSkipFirstRender } from '@libs/utils';
import { CardBalanceActionName } from './utils/utils';

const getActionByType = (actions: TWAction[] | undefined, actionType: string | ActionActionTypeEnum) => actions?.find((f) => f.actionType === actionType);

export const CardBalance = ({ widget }: TWidgetProps<TCardBalanceData>) => {
    const { triggerAction, triggerActionsByType } = useTriggerAction(widget);
    const { isHidden } = useBehavior(widget);
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const messages = data.messages ?? ({} as TCardBalanceDataMessages);
    const status = data.status;
    const isExpired = status ? status === ECardBalanceStatus.EXPIRED : (data.expired ?? false);
    const isFrozen = status ? status === ECardBalanceStatus.FROZEN : (data.frozen ?? false);
    const suspiciousActivity = status ? status === ECardBalanceStatus.SUSPICIOUS_ACTIVITY : (data.suspiciousActivity ?? false);
    const isKycInProgress = status === ECardBalanceStatus.KYC_IN_PROGRESS;
    const cardIssued = status ? !isKycInProgress && status !== ECardBalanceStatus.NOT_ISSUED : (data.isCardIssued ?? false);
    const appleWalletAdded = data.appleWalletAdded ?? false;
    const googleWalletAdded = data.googleWalletAdded ?? false;
    const metaText = messages.metaText;

    const [isCardScreen, setCardScreen] = useState(false);
    const firstRender = useSkipFirstRender(isCardScreen);
    const actionAutoExecute = useMemo(() => getActionByType(widget.actions, CardBalanceActionName.autoExecute), [widget.actions]);
    const onTriggerAction = useEffectEvent((action: TWAction) => {
        return triggerAction(action);
    });

    useEffect(() => {
        if (actionAutoExecute) {
            onTriggerAction(actionAutoExecute).catch(console.error);
        }
    }, [actionAutoExecute]);

    const onCardClick = (isCardIssued: boolean, isActionAutoExecute: boolean) => {
        if (isCardIssued) {
            setCardScreen(true);
        } else if (!isActionAutoExecute) {
            triggerActionsByType(CardBalanceActionName.getCard, { isShowLoader: true }).catch(console.error);
        }
    };

    if (isHidden) {
        return false;
    }

    return (
        <>
            {!isCardScreen && (
                <div
                    data-test={widget.uid}
                    className={styles.paysendCardContainer}
                    style={getInlineStyles({ margin: options.margin })}
                    onClick={() => onCardClick(cardIssued, !!actionAutoExecute)}
                >
                    <PaysendCard
                        isFirstRender={firstRender}
                        isFrozen={isFrozen || suspiciousActivity}
                        isExpired={isExpired}
                        isKycInProgress={isKycInProgress}
                        metaText={metaText}
                        appleWalletAdded={appleWalletAdded}
                        googleWalletAdded={googleWalletAdded}
                        maskedPan={data.maskedPan}
                        isCardIssued={cardIssued}
                        getCard={messages.getCard}
                    />
                </div>
            )}
            <div className={clsx(styles.cardScreen, isCardScreen && styles.cardScreenOnCardScreen)} data-test={'card-details-' + widget.uid}>
                <div className={styles.layoutContainer}>
                    <div className={clsx(styles.topLayout, isCardScreen && styles.topLayoutOnCardScreen)}>
                        <div className={styles.layoutInner}>
                            <PaysendCard
                                isCardScreen
                                isFrozen={isFrozen || suspiciousActivity}
                                isExpired={isExpired}
                                metaText={metaText}
                                appleWalletAdded={appleWalletAdded}
                                googleWalletAdded={googleWalletAdded}
                                maskedPan={data.maskedPan}
                                isCardIssued={cardIssued}
                                getCard={messages.getCard}
                            />
                            <div className={styles.textContainer}>
                                <TextCommon
                                    isWidget={false}
                                    content={messages.cardDescription}
                                    options={{
                                        textColor: isFrozen || isExpired ? 'grey_72_lc' : 'black',
                                        textAlign: 'center',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={clsx(styles.bottomLayout, isCardScreen && styles.bottomLayoutCardScreen)}>
                        <Dash onTouchEnd={() => setCardScreen(false)} />
                        <div className={styles.layoutInner}>
                            <CardBottomButtons
                                widget={widget}
                                suspiciousActivity={suspiciousActivity}
                                isExpired={isExpired}
                                isFrozen={isFrozen}
                                messages={messages}
                                closeScreen={() => setCardScreen(false)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
